<template>
    <div class="ScenarioNodeSelector" v-click-outside="hideMenu">
        <div class="search-container">
            <div class="small-item centerXY" @click="openScenarioAdvanceSearch">
                <v-icon large>mdi-menu-down</v-icon>
            </div>
            <input
                v-model="searchKey"
                :placeholder="this.$f.tc('search') + ' | Ctrl + Shift + F'"
                @focusin="showMenu"
                ref="input"
            />
            <div class="small-item centerXY" @click="clearSearch" v-if="searchKey">
                <v-icon class="clear-icon">mdi-close</v-icon>
            </div>
            <v-tooltip bottom>
                <template v-slot:activator="props">
                    <div class="small-item centerXY" v-on="props.on" @click="toggleAccent">
                        {{ useAccent ? 'Âa' : 'âA' }}
                    </div>
                </template>
                <div>
                    Âa: {{ $t('useVietnameseAccent') }} <br>
                    âA: {{ $t('doNotUseVietnameseAccent') }}
                </div>
            </v-tooltip>
        </div>
        <div class="menu fci-list fci-scrollbar" :class="{active: show}">
            <div
                class="spreadXY centerXY empty-notes"
                v-if="scenarioList.length < 1 && stepList.length < 1 && cardList.length < 1"
            >
                Search result for <br>
                scenario name, <br>
                step name, <br>
                card name and card text
            </div>
            <div class="fci-list-group" v-if="scenarioList.length > 0">
                {{ $f.tc('scenario') }}
            </div>
            <div
                v-for="scenario in scenarioList"
                :key="scenario.code"
                @mousedown="selectScenario(scenario)"
                class="fci-list-item"
            >
                {{ scenario.name }}
            </div>
            <div class="fci-list-group" v-if="stepList.length > 0">
                {{ $f.tc('step') }}
            </div>
            <div
                v-for="step in stepList"
                :key="step.code"
                @mousedown="selectStep(step)"
                class="fci-list-item"
            >
                {{ step.name }}
            </div>
            <div class="fci-list-group" v-if="cardList.length > 0">
                {{ $f.tc('card') }}
            </div>
            <div
                v-for="card in cardList"
                :key="card.code"
                @mousedown="selectCard(card)"
                class="fci-list-item"
                :class="{active: card.code === activeCardCode}"
            >
                <v-icon small class="mr-1">{{ MapCardTypeToData[card.card_type_id].icon }}</v-icon>
                {{ card.name }}
                <v-icon small v-if="!card.activate" class="ml-2">mdi-lightbulb-off</v-icon>
            </div>
        </div>
    </div>
</template>

<script>

import { mapState } from 'vuex'
import { CardType, MapCardTypeToData } from '@/utils/const'
import { AppEventBusType } from '@/eventbus/app.eventbus'

export default {
    name: 'ScenarioNodeFinder',
    data () {
        return {
            MapCardTypeToData,
            show: false,
            searchKey: '',
            scenarioList: [],
            stepList: [],
            cardList: [],
            useAccent: false
        }
    },
    computed: {
        ...mapState({
            allScenarioList: state => state.scenario.scenarioList,
            allStepList: state => state.scenario.stepList,
            allCardList: state => state.scenario.cardList,
            activeCardCode: state => state.scenario.activeCardCode
        })
    },
    mounted () {
        window.addEventListener('keypress', this.searchScenario)
    },
    destroyed () {
        window.removeEventListener('keypress', this.searchScenario)
    },
    methods: {
        searchScenario (event) {
            if (event.ctrlKey && event.shiftKey && event.keyCode === 6) {
                event.preventDefault()
                this.$refs.input.focus()
            }
        },
        selectScenario (scenario) {
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeScenarioCode: scenario.code
            })
        },
        selectStep (step) {
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeStepCode: step.code
            })
        },
        selectCard (card) {
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeCardCode: card.code
            })
        },
        clearSearch () {
            this.searchKey = ''
            this.stepList = []
            this.cardList = []
        },
        showMenu () {
            this.show = true
        },
        hideMenu () {
            this.show = false
        },
        getSearchValue (text) {
            if (!text) {
                return ''
            }
            return this.useAccent ? text.trim().toLowerCase() : this.$ut.noAccent(text)
        },
        updateSearchResult () {
            let searchKey = this.searchKey.trim()
            if (!searchKey) {
                this.scenarioList = []
                this.stepList = []
                this.cardList = []
                return
            }
            searchKey = this.getSearchValue(searchKey)
            this.scenarioList = this.allScenarioList.filter(
                node => this.getSearchValue(node.name).includes(searchKey)
            )
            this.stepList = this.allStepList.filter(
                node => this.getSearchValue(node.name).includes(searchKey)
            )
            this.cardList = this.allCardList.filter(card => {
                if (this.getSearchValue(card.name).includes(searchKey)) {
                    return true
                }
                if (
                    card.card_type_id === CardType.TEXT ||
                    card.card_type_id === CardType.QUICK_REPLY
                ) {
                    if (this.getSearchValue(card.config.text).includes(searchKey)) {
                        return true
                    }
                }
                return false
            })
        },
        toggleAccent () {
            this.useAccent = !this.useAccent
            this.updateSearchResult()
        },
        openScenarioAdvanceSearch () {
            this.$appService.useEventBus(AppEventBusType.OPEN_SCENARIO_ADVANCE_SEARCH, null)
        }
    },
    watch: {
        searchKey: 'updateSearchResult'
    }
}
</script>

<style scoped lang="scss">
.ScenarioNodeSelector {
    position: relative;
}

.menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 0;
    overflow: auto;
    background: #fff;
    box-shadow: 3px 3px 6px #0000000D;
    z-index: 1;
    opacity: 0.5;
    transition: all 0.3s ease;

    &.active {
        height: 400px;
        opacity: 1;
    }
}

input {
    width: 100%;
    border-left: 1px solid #EDEDF2;
    height: 40px;
    padding: 5px 10px;
    font-size: 16px;
    outline: none;
    flex: 1 1 100px;
    user-select: none;
}

.search-container {
    position: relative;
    display: flex;
    border: 1px solid #EDEDF2;
    border-radius: 5px;

    .small-item {
        width: 40px;
        flex: 0 0 40px;
        color: #acacd2;
        font-size: 1.5em;
        cursor: pointer;

        &:hover {
            background: #EDEDF2;
        }

        &:last-child {
            border-left: 1px solid #EDEDF2;
        }
    }
}

.search-icon {
    position: absolute;
    top: 7px;
    left: 10px;
}

.clear-icon {
    color: #acacd2;
}

.empty-notes {
    color: #deb4bb;
}
</style>
